import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, CalendarClock, BellRing, Lock, TrendingUp, Download } from "lucide-react";

const STATUS = {
  expired: { label: "Expired", cls: "bg-red-100 text-red-700" },
  due: { label: "Due — no reminder yet", cls: "bg-amber-100 text-amber-700" },
  reminded: { label: "Reminded", cls: "bg-sky-100 text-sky-700" },
  held: { label: "Spot held", cls: "bg-emerald-100 text-emerald-700" },
};

export default function RenewalsPanel() {
  const [data, setData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [grace, setGrace] = useState("");
  const [autoHold, setAutoHold] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [savingGrace, setSavingGrace] = useState(false);
  const [wonBack, setWonBack] = useState([]);
  const [board, setBoard] = useState([]);

  const load = () => {
    api.get("/admin/renewals").then((r) => setData(r.data)).catch(() => setData({ renewals: [] }));
    api.get("/admin/renewals/metrics").then((r) => setMetrics(r.data)).catch(() => setMetrics(null));
    api.get("/admin/renewals/settings").then((r) => { setGrace(String(r.data.grace_days ?? "")); setAutoHold(!!r.data.auto_hold); }).catch(() => {});
    api.get("/admin/renewals/won-back").then((r) => setWonBack(r.data.won_back || [])).catch(() => {});
    api.get("/admin/renewals/leaderboard").then((r) => setBoard(r.data.leaderboard || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveGrace = async () => {
    setSavingGrace(true);
    try { const { data } = await api.post("/admin/renewals/settings", { grace_days: Number(grace) }); setGrace(String(data.grace_days)); toast.success(`Rebook hold set to ${data.grace_days} days`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSavingGrace(false); }
  };
  const toggleAutoHold = async (val) => {
    setAutoHold(val);
    try { const { data } = await api.post("/admin/renewals/settings", { auto_hold: val }); setAutoHold(!!data.auto_hold); toast.success(`Auto-hold ${data.auto_hold ? "on" : "off"}`); }
    catch (e) { setAutoHold(!val); toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const remind = async (r) => {
    setBusyId(r.business_id + ":remind");
    try { const { data } = await api.post(`/admin/renewals/${r.business_id}/remind`); toast.success(`Reminder sent${data.texted ? " (email + text)" : " (email)"}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send reminder"); }
    finally { setBusyId(null); }
  };
  const hold = async (r) => {
    setBusyId(r.business_id + ":hold");
    try { const { data } = await api.post(`/admin/renewals/${r.business_id}/extend-hold`); toast.success(`Spot held through ${new Date(data.reserved_until).toLocaleDateString()}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not hold spot"); }
    finally { setBusyId(null); }
  };

  const fmt = (s) => (s ? new Date(s).toLocaleDateString() : "—");
  const rows = (data && data.renewals) || [];

  const exportCsv = () => {
    const cols = ["Business", "Email", "Phone", "Rep", "Expires", "Days left", "Status", "Reminders"];
    const lines = [cols.join(",")];
    rows.forEach((r) => {
      const vals = [r.name, r.email || "", r.phone || "", r.rep || "", r.expires ? new Date(r.expires).toLocaleDateString() : "", r.days_left, r.status, (r.reminders_sent || []).map((k) => k.split(":")[0]).join(" ")];
      lines.push(vals.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `sam-renewals-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <div className="py-10 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-sam-crimson" /></div>;

  return (
    <div className="space-y-5" data-testid="renewals-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sam-navy font-display text-lg font-bold"><CalendarClock className="h-5 w-5 text-sam-crimson" />Renewals due</div>
        <span className="text-xs text-sam-slate">Premium postcard deals expiring in the next 60 days · {rows.length} business{rows.length === 1 ? "" : "es"}</span>
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportCsv} data-testid="renewals-export-btn"><Download className="h-4 w-4 mr-1" />Export CSV</Button>
      </div>

      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-slate-50/60 p-3">
        <div className="flex items-center gap-2" data-testid="grace-setting">
          <span className="text-xs text-sam-slate">Rebook hold (days)</span>
          <Input type="number" min={1} max={60} value={grace} onChange={(e) => setGrace(e.target.value)} className="w-20 h-8" data-testid="grace-input" />
          <Button size="sm" variant="outline" onClick={saveGrace} disabled={savingGrace} data-testid="grace-save-btn">{savingGrace && <Loader2 className="h-3 w-3 animate-spin mr-1" />}Save</Button>
        </div>
        <div className="flex items-center gap-2" data-testid="autohold-setting">
          <Switch checked={autoHold} onCheckedChange={toggleAutoHold} data-testid="autohold-switch" />
          <span className="text-xs text-sam-slate">Auto-hold spot when the 15-day reminder is sent</span>
        </div>
      </div>

      {metrics && (
        <div className="rounded-xl border border-border bg-slate-50/60 p-4" data-testid="renewal-metrics">
          <div className="flex items-center gap-2 text-sam-navy font-semibold mb-3"><TrendingUp className="h-4 w-4 text-sam-green" />Renewed vs lapsed
            <span className="text-xs font-normal text-sam-slate">· overall {metrics.overall_rate}% renewed ({metrics.renewed} renewed / {metrics.lapsed} lapsed, last 6 months){metrics.won_back ? ` · ${metrics.won_back} won back` : ""}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {(metrics.series || []).length === 0 && <span className="text-xs text-sam-slate">No expiries in the last 6 months yet.</span>}
            {(metrics.series || []).map((m) => (
              <div key={m.month} className="rounded-lg border border-border bg-white px-3 py-2 min-w-[92px]" data-testid={`metric-${m.month}`}>
                <div className="text-xs text-sam-slate">{m.month}</div>
                <div className="text-lg font-bold text-sam-navy">{m.rate}%</div>
                <div className="text-[11px] text-sam-slate">{m.renewed}✓ / {m.lapsed}✕</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-sam-cream/60 text-sam-slate text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left p-3">Business</th>
              <th className="text-left p-3">Rep</th>
              <th className="text-left p-3">Expires</th>
              <th className="text-left p-3">Days left</th>
              <th className="text-left p-3">Reminders</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-sam-slate">No upcoming renewals.</td></tr>}
            {rows.map((r, i) => {
              const st = STATUS[r.status] || STATUS.due;
              return (
                <tr key={r.business_id} className="border-t border-border" data-testid={`renewal-row-${i}`}>
                  <td className="p-3"><div className="font-semibold text-sam-navy">{r.name}</div><div className="text-xs text-sam-slate">{r.email || r.phone || "—"}</div></td>
                  <td className="p-3 text-sam-slate" data-testid={`renewal-rep-${i}`}>{r.rep || "—"}{r.rep && r.rep_source === "referrer" ? <span className="text-[10px] text-sam-slate ml-1">(ref)</span> : ""}</td>
                  <td className="p-3 text-sam-slate">{fmt(r.expires)}</td>
                  <td className="p-3 font-semibold text-sam-navy">{r.days_left < 0 ? `${Math.abs(r.days_left)}d ago` : `${r.days_left}d`}</td>
                  <td className="p-3 text-sam-slate">{(r.reminders_sent || []).map((k) => k.split(":")[0]).join(", ") || "—"}</td>
                  <td className="p-3"><span className={`rounded-full text-xs font-semibold px-2 py-0.5 ${st.cls}`} data-testid={`renewal-status-${i}`}>{st.label}{r.status === "held" && r.hold_until ? ` → ${fmt(r.hold_until)}` : ""}</span>{r.won_back ? <span className="ml-1 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold px-2 py-0.5" data-testid={`renewal-wonback-${i}`}>Won back</span> : ""}</td>
                  <td className="p-3">
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="h-8" onClick={() => remind(r)} disabled={busyId === r.business_id + ":remind"} data-testid={`renewal-remind-${i}`}>{busyId === r.business_id + ":remind" ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellRing className="h-3 w-3" />}<span className="ml-1 hidden sm:inline">Remind</span></Button>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => hold(r)} disabled={busyId === r.business_id + ":hold"} data-testid={`renewal-hold-${i}`}>{busyId === r.business_id + ":hold" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />}<span className="ml-1 hidden sm:inline">Hold</span></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {board.length > 0 && (
        <div className="rounded-xl border border-border bg-slate-50/60 p-4" data-testid="rep-leaderboard">
          <div className="text-sam-navy font-semibold mb-3">Rep leaderboard — renewals saved</div>
          <div className="flex flex-wrap gap-2">
            {board.map((e, i) => (
              <div key={e.rep} className="rounded-lg border border-border bg-white px-3 py-2 min-w-[150px]" data-testid={`leaderboard-row-${i}`}>
                <div className="font-semibold text-sam-navy text-sm">{i === 0 ? "🏆 " : ""}{e.rep}</div>
                <div className="text-xs text-sam-slate">{e.active} active · {e.won_back} won back</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {wonBack.length > 0 && (
        <div className="rounded-xl border border-border overflow-x-auto" data-testid="winback-report">
          <div className="bg-sam-cream/60 px-3 py-2 text-sam-navy font-semibold text-sm">Won back — lapsed advertisers who returned</div>
          <table className="w-full text-sm">
            <thead className="text-sam-slate text-xs uppercase tracking-wide"><tr><th className="text-left p-3">Business</th><th className="text-left p-3">Rep</th><th className="text-left p-3">Returned</th><th className="text-left p-3">Gap</th></tr></thead>
            <tbody>
              {wonBack.map((w, i) => (
                <tr key={w.business_id} className="border-t border-border" data-testid={`winback-row-${i}`}>
                  <td className="p-3"><div className="font-semibold text-sam-navy">{w.name}</div><div className="text-xs text-sam-slate">{w.email || "—"}</div></td>
                  <td className="p-3 text-sam-slate">{w.rep || "—"}</td>
                  <td className="p-3 text-sam-slate">{fmt(w.won_back_at)}</td>
                  <td className="p-3 font-semibold text-sam-navy">{w.gap_days != null ? `${w.gap_days}d away` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
